import { ApiError } from './errors';
import type { ApiErrorBody } from './types';

/** Empty in dev (Vite proxies /api), or the API origin in a production build. */
export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

const TOKEN_KEY = 'icp.token';

interface StoredToken {
  token: string;
  expiresAt: number;
}

export const tokenStore = {
  get(): string | null {
    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      if (!raw) return null;
      const t = JSON.parse(raw) as StoredToken;
      if (Date.now() >= t.expiresAt) {
        localStorage.removeItem(TOKEN_KEY);
        return null;
      }
      return t.token;
    } catch {
      return null;
    }
  },
  set(token: string, expiresInSeconds: number): void {
    try {
      // Treat the token as expired a little early so a request never races its expiry.
      const value: StoredToken = { token, expiresAt: Date.now() + (expiresInSeconds - 30) * 1000 };
      localStorage.setItem(TOKEN_KEY, JSON.stringify(value));
    } catch {
      /* storage unavailable: the session lasts until reload */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};
/** Called on any 401 from an authenticated call (no refresh tokens: back to login). */
export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  onUnauthorized = fn;
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

interface RequestOptions {
  method?: string;
  query?: Query;
  json?: unknown;
  form?: FormData | URLSearchParams;
  auth?: boolean;
  signal?: AbortSignal;
}

async function send(path: string, opts: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const auth = opts.auth ?? true;
  if (auth) {
    const token = tokenStore.get();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  let body: BodyInit | undefined;
  if (opts.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.json);
  } else if (opts.form) {
    // The browser sets the multipart boundary / urlencoded type itself.
    body = opts.form;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${withQuery(path, opts.query)}`, {
      method: opts.method ?? (body ? 'POST' : 'GET'),
      headers,
      body,
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, null);
  }

  if (!res.ok) {
    let detail: ApiErrorBody['detail'] | null = null;
    try {
      detail = ((await res.json()) as ApiErrorBody).detail ?? null;
    } catch {
      /* non-JSON error body */
    }
    if (res.status === 401 && auth) {
      tokenStore.clear();
      onUnauthorized();
    }
    throw new ApiError(res.status, detail);
  }
  return res;
}

export async function api<T>(path: string, opts?: RequestOptions): Promise<T> {
  const res = await send(path, opts);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function apiBlob(path: string, opts?: RequestOptions): Promise<Blob> {
  const res = await send(path, opts);
  return res.blob();
}
